import {Component, OnDestroy, OnInit} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {ActivatedRoute} from '@angular/router';
import {notNull} from '../../shared/utils';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  ClientEditComponent_getClient,
  ClientEditComponent_getClient_user,
  ClientEditComponent_getClientVariables,
} from './__generated__/ClientEditComponent_getClient';
import {ToastrService} from 'ngx-toastr';
import {
  ClientEditComponent_editClient,
  ClientEditComponent_editClientVariables,
} from './__generated__/ClientEditComponent_editClient';
import {TranslateService} from '@ngx-translate/core';
import {Language} from '../../new-client/new-client.component';
import {LANGUAGES} from '../../shared/graphql/globalTypes';
import {catchError} from 'rxjs/operators';
import {ErrorService} from 'src/app/shared/services/error.service';

const GET_CLIENT = gql`
  query ClientEditComponent_getClient($id: ID!) {
    user(id: $id) {
      id # Required
      username # Phone number in case of clients
      firstName
      lastName
      language
      movementsEnabled
      paused
      archived
    }
  }
`;

const EDIT_CLIENT = gql`
  mutation ClientEditComponent_editClient($input: UserMutationInput!) {
    createOrUpdateUser(input: $input) {
      user {
        firstName
        lastName
        id
        language
        movementsEnabled
        paused
        archived
      }
    }
  }
`;

@Component({
  selector: 'app-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['./client-edit.component.scss'],
})
export class ClientEditComponent implements OnInit, OnDestroy {
  private userId: string | null = null;

  private subscriptions: Subscription[] = [];

  editForm = new FormGroup({
    firstName: new FormControl(null),
    lastName: new FormControl(null),
    language: new FormControl(null, Validators.required),
    movementAssignmentSkipAble: new FormControl(null, Validators.required),
    state: new FormControl(null, Validators.required),
  });

  user: ClientEditComponent_getClient_user | null = null;

  languages: Language[] = [
    Language.NL,
    // Language.EN,
    Language.DE,
  ];

  constructor(
    private apollo: Apollo,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private translate: TranslateService,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    const languageTranslationMap: {[key in Language]: string} = {
      [Language.NL]: this.translate.instant('NL', Language.NL),
      // [Language.EN]: this.translate.instant('ENGLISH', Language.EN),
      [Language.DE]: this.translate.instant('DE', Language.DE),
    };

    this.languages.forEach(lang => languageTranslationMap[lang]);

    this.userId = notNull(this.route.parent).snapshot.params['id'];

    this.subscriptions.push(
      this.apollo
        .watchQuery<ClientEditComponent_getClient, ClientEditComponent_getClientVariables>({
          query: GET_CLIENT,
          variables: {id: notNull(this.userId)},
        })
        .valueChanges.pipe(catchError(error => this.errorHandler.handleApollo(error)))
        .subscribe(results => {
          this.user = results.data.user;
          this.editForm.get('firstName')?.setValue(results.data.user?.firstName);
          this.editForm.get('lastName')?.setValue(results.data.user?.lastName);

          if (results.data.user?.language === LANGUAGES.nl) {
            this.editForm.controls['language'].setValue(Language.NL);
          } else if (results.data.user?.language === LANGUAGES.de) {
            this.editForm.controls['language'].setValue(Language.DE);
          }

          this.editForm
            .get('movementAssignmentSkipAble')
            ?.setValue(results.data.user?.movementsEnabled);
          this.editForm.get('state')?.setValue(this.getDerivedUserState(results.data.user));
        }),
    );
  }

  private getDerivedUserState(user: ClientEditComponent_getClient_user | null) {
    if (!user) {
      return null;
    }

    let state = 'active';
    if (user.paused) {
      state = 'paused';
    }
    if (user.archived) {
      state = 'archived';
    }

    return state;
  }

  private getPaused() {
    let state: string | null = null;
    const value = this.editForm.get('state')?.value;
    if (value) {
      state = value as string;
    } else {
      return !!this.user?.paused;
    }

    return state === 'paused' || state === 'archived';
  }

  private getArchived() {
    let state: string | null = null;
    const value = this.editForm.get('state')?.value;
    if (value) {
      state = value;
    } else {
      return !!this.user?.archived;
    }

    return state === 'archived';
  }

  onEdit() {
    if (!this.user) {
      return;
    }
    const firstNameValue = this.editForm.get('firstName')?.value;
    const lastNameValue = this.editForm.get('lastName')?.value;
    const languageValue = this.editForm.get('language')?.value;
    const movementEnabled = this.editForm.get('movementAssignmentSkipAble')?.value;

    this.subscriptions.push(
      this.apollo
        .mutate<ClientEditComponent_editClient, ClientEditComponent_editClientVariables>({
          mutation: EDIT_CLIENT,
          variables: {
            input: {
              id: this.user.id,
              firstName: !firstNameValue ? this.user.firstName : firstNameValue,
              lastName: !lastNameValue ? this.user.lastName : lastNameValue,
              language: !languageValue ? this.user.language : languageValue.toLowerCase(),
              movementsEnabled: !movementEnabled ? this.user.movementsEnabled : movementEnabled,
              paused: this.getPaused(),
              archived: this.getArchived(),
            },
          },
          update: cache => cache.evict({fieldName: 'clients'}),
        })
        .subscribe(() => {
          this.toastr.success(this.translate.instant('CHANGES_HAVE_BEEN_SAVED'), '', {
            timeOut: 2000,
          });
        }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
